import { BrowserModule } from "@angular/platform-browser";
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { SharedModule } from "./shared/shared.module";
import { AppRoutingModule } from "./app-routing.module";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";
import { AppComponent } from "./app.component";
import { LoginComponent } from "./auth/login/login.component";
import { ToastrModule } from "ngx-toastr";
import { AngularFireModule } from "@angular/fire";
import { AngularFireMessagingModule } from "@angular/fire/messaging";
import { environment } from "../environments/environment";

import { CookieService } from "ngx-cookie-service";

import { MAT_DATE_LOCALE } from "@angular/material/core";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from "@angular/material-moment-adapter";
import {
  NgxMatMomentModule,
  NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS
} from '@angular-material-components/moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDatetimePickerModule,
  NgxMatTimepickerModule,
  NgxMatDateAdapter
} from '@angular-material-components/datetime-picker';
import { NgxMatMomentAdapter } from '@angular-material-components/moment-adapter';

import * as moment from 'moment';
moment.locale('vi');

import { RedirectComponent } from "./shared/components/redirect/redirect.component";
import { ConfirmDialogComponent } from "./pages/confirm-dialog/confirm-dialog.component";
import { EventEmitterService } from "./shared/services/event.emitter.service";
import { ErrorsHandler } from "./shared/interceptors/errors-handler";
import { AuthInterceptor } from "./shared/interceptors/auth-interceptor";
import { NgxSpinnerModule } from "ngx-spinner";
import {
  MAT_DIALOG_SCROLL_STRATEGY,
  MatDialogConfig,
  MatDialogModule,
} from "@angular/material/dialog";
import { SalesComponent } from "./sales/sales/sales.component";
import { OrderContentDynamicComponent } from "./sales/order-content-dynamic/order-content-dynamic.component";
import { Sale1Component } from "./sale1/sale1.component";
import { TabsComponent } from "./sale1/tabs/tab.scomponent";
import { TabComponent } from "./sale1/tabs/tab.component";
import { DynamicTabsDirective } from "./sale1/tabs/dynamic-tabs.directive";
import { DynamicTabsDirective as SharedDynamicTabsDirective } from "./shared/directives/dynamic-tabs.directive";
import { PersonEditComponent } from "./sale1/people/person-edit.component";
import { PeopleListComponent } from "./sale1/people/people-list.component";
import { AddNewHangHoaComponent } from "./sales/add-new-hang-hoa/add-new-hang-hoa.component";
// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}
import { NgxPrintModule } from "ngx-print";
import { TooltipDirective } from "./shared/directives/tooltip.directive";
import { DanhSachDonHangComponent } from "./sales/danh-sach-don-hang/danh-sach-don-hang.component";
import { DetailOrderCustomerComponent } from "./sales/detail-order-customer/detail-order-customer.component";
import { Overlay } from "@angular/cdk/overlay";
import { MatTabsModule } from "@angular/material/tabs";
import { AddCustomerDrawerComponent } from "./sales/add-customer-drawer/add-customer-drawer.component";
import { AddNewHangHoaDrawerComponent } from "./sales/add-new-hang-hoa-drawer/add-new-hang-hoa-drawer.component";
import { ThongTinCaNhanModule } from "./module/thong-tin-ca-nhan/thong-tin-ca-nhan.module";

// Cấu hình định dạng chuẩn cho cả moment và ngx-mat
export const CUSTOM_MOMENT_FORMATS = {
  parse: {
    dateInput: "DD/MM/YYYY HH:mm:ss",
  },
  display: {
    dateInput: "DD/MM/YYYY HH:mm:ss",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RedirectComponent,
    ConfirmDialogComponent,
    SalesComponent,
    OrderContentDynamicComponent,
    Sale1Component,
    TabsComponent,
    TabComponent,
    DynamicTabsDirective,
    SharedDynamicTabsDirective,
    PersonEditComponent,
    PeopleListComponent,
    AddNewHangHoaComponent,
    TooltipDirective,
    DanhSachDonHangComponent,
    DetailOrderCustomerComponent,
    AddCustomerDrawerComponent,
    AddNewHangHoaDrawerComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    SharedModule,
    AppRoutingModule,
    HttpClientModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgxSpinnerModule,
    MatDialogModule,
    NgxPrintModule,
    MatTabsModule,
    MatDatepickerModule,
    MatMomentDateModule,
    NgxMatMomentModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    ThongTinCaNhanModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireMessagingModule
  ],
  providers: [
    CookieService,
    EventEmitterService,
    {
      provide: ErrorHandler,
      useClass: ErrorsHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: "vi" },
    // Cấu hình để hiển thị đúng giờ địa phương (Local Time)
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_MOMENT_FORMATS },
    // Khai báo lại adapter để đảm bảo no-UTC được áp dụng triệt để
    {
      provide: NgxMatDateAdapter,
      useClass: NgxMatMomentAdapter,
      deps: [MAT_DATE_LOCALE, NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    moment.locale('vi');
  }
}
